/**
 * @file OrderNowStrip.tsx
 * @description Полоса «Сейчас» под плитками шапки заказа
 * (phase19.24-order-card-chain-design.md §13, §14).
 *
 * Отвечает на три вопроса менеджера в одном месте: где заказ (стадия и сколько
 * в ней стоит), чей ход (стикер `OwnerChip`), что делать мне (одна фраза).
 * При чужом ходе — строка «Ваш следующий шаг», чтобы очередь была видна
 * заранее.
 *
 * Ритм — как у `NoticeStrip` (левая полоска 4px + мягкий фон), но тон
 * нейтральный: красный только для проблемы и просрочки. Приоритет цвета —
 * `danger` > «ваш ход» > цвет владельца (реестр `order-chain-tone.ts`).
 *
 * Стикеров не больше двух (§14): один красный по приоритету (проблема важнее
 * просрочки) и один владельца. Три бейджа подряд («Просрочено», «Нужно
 * решение», «Ваш ход») читались как шум — дефект QA 2026-09-21.
 *
 * Кнопки в полосе нет (phase19.31, SC52): главное действие одно — в верхней
 * панели карточки, на телефоне — в нижней полосе каркаса. До этого «Отметить
 * оплату» стояла и в шапке, и здесь.
 */

import { ArrowRight } from "lucide-react";

import { OwnerChip } from "./OwnerChip";
import { ownerMeta } from "../../lib/order-chain-owner";
import type { NowStripTone } from "../../lib/order-chain-tone";
import type { OrderNowModel } from "../../lib/order-now";
import { cn } from "@/lib/utils";

interface Props {
  now: OrderNowModel;
}

/** Фон полосы по тону. Владелец подставляет свой фон отдельно. */
const TONE_SURFACE: Record<NowStripTone, string> = {
  danger: "bg-destructive/8",
  manager: "bg-primary/8",
  owner: "bg-surface-low",
};

/** Цвет левой полоски 4px. */
const TONE_EDGE: Record<NowStripTone, string> = {
  danger: "bg-destructive",
  manager: "bg-primary",
  owner: "bg-on-surface-variant",
};

export function OrderNowStrip({ now }: Props) {
  const isDanger = now.tone === "danger";
  const edge = now.tone === "owner" ? ownerMeta(now.owner).edge : TONE_EDGE[now.tone];
  // Просрочка при проблеме показывается словами во второй строке: стикер
  // занят «Нужно решение» — оно важнее срока.
  const overdueNote = now.alert === "PROBLEM" ? now.overdueLabel : null;

  return (
    <section
      aria-label={now.ariaLabel}
      className={cn("flex overflow-hidden rounded-xl", TONE_SURFACE[now.tone])}
    >
      {/* Акцентная полоска 4px — как у NoticeStrip: полоса и текущий узел
          «Пути» читаются как одно событие. */}
      <span aria-hidden className={cn("w-1 shrink-0", edge)} />
      <div className="flex min-w-0 flex-1 flex-col gap-3 px-4 py-3 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            {/* Не больше двух стикеров (§14): один красный по приоритету, за
                ним — владелец хода. Срок при проблеме уходит во вторую строку. */}
            {now.alert ? (
              <OwnerChip alert={now.alert} label={now.alertLabel ?? undefined} />
            ) : null}
            <OwnerChip owner={now.owner} outlined={now.ownerOutlined} />
            <span className="min-w-0 text-base font-medium">
              {now.stage.name}
              {now.sinceLabel ? (
                <span className="text-on-surface-variant"> · {now.sinceLabel}</span>
              ) : null}
            </span>
          </div>

          <p className={cn("text-base", isDanger && "text-destructive")}>
            {now.text.first}
          </p>

          {now.text.second || now.nextStepHint || overdueNote ? (
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-on-surface-variant">
              {now.text.second}
              {/* Срок у проблемы — текстом: третьего стикера в полосе не бывает. */}
              {overdueNote ? (
                <span className="font-medium text-destructive">{overdueNote}</span>
              ) : null}
              {now.nextStepHint ? (
                // Зелёным и со стрелкой: взгляд ведёт от «где я» к «где мой ход».
                <span className="inline-flex items-center gap-1 font-medium text-primary">
                  <ArrowRight className="size-4" aria-hidden />
                  {now.nextStepHint}
                </span>
              ) : null}
            </p>
          ) : null}
        </div>

      </div>
    </section>
  );
}
